// === Contractors — Settings → Contractors ===
//
// A company's contractors (labour hire etc.) and their workers (the "Staff"
// tab). Each worker is a staff profile with no sign-in on a no-sign-in job
// title, so they can be picked as labour and get timesheet hours; those hours
// show under Contractors → Timesheet. See supabase/migrations/20261014_contractors.sql.

import { z } from 'zod';

/** Blank means "not set" — sent as null. */
const text = (max: number) => z.string().trim().max(max).nullable().optional();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date').nullable().optional();
const email = z.string().trim().email('Enter a valid email').max(200).nullable().optional();

/**
 * A contractor's own job title + pay rate (the Titles tab). Workers on it are
 * costed at THIS rate, not the company title's. `key` lets a worker in the same
 * save point at a title that has no id yet ('id-5' for saved ones, anything
 * unique for new ones).
 */
export const ContractorTitleInputSchema = z.object({
  id:            z.number().int().positive().optional(),
  key:           z.string().min(1).max(50),
  title:         z.string().trim().min(1, 'Every title needs a name').max(200),
  /** $/hour; null = not set (labour stays uncosted, never defaulted). */
  pay_rate:      z.number().nonnegative().max(10000).nullable(),
  /** The company no-sign-in title the worker's profile sits on (award rules, sign-in). */
  base_title_id: z.number().int().positive(),
});
export type ContractorTitleInput = z.infer<typeof ContractorTitleInputSchema>;

export const ContractorStaffInputSchema = z.object({
  /** Present for an existing worker; absent for a new one. */
  id:         z.number().int().positive().optional(),
  first_name: z.string().trim().min(1, 'Worker first name is required').max(100),
  last_name:  z.string().trim().min(1, 'Worker last name is required').max(100),
  /** The `key` of one of this contractor's titles — sets their pay rate. */
  title_key:  z.string().min(1, 'Pick a job title for each worker').max(50),
  phone:      text(50),
  // Only first name, last name and job title are checked for a worker.
  email:      text(200),
  licence:    text(200),
  notes:      text(2000),
});
export type ContractorStaffInput = z.infer<typeof ContractorStaffInputSchema>;

export const ContractorUpsertSchema = z.object({
  name:                z.string().trim().min(1, 'Contractor name is required').max(300),
  abn:                 z.string().trim().regex(/^(\d\s?){11}$/, 'ABN is 11 digits').nullable().optional(),
  address:             text(500),
  suburb:              text(200),
  state:               text(50),
  postcode:            text(10),
  phone:               text(50),
  email,
  contact_name:        text(200),
  contact_mobile:      text(50),
  insurer:             text(200),
  insurance_policy:    text(200),
  insurance_expiry:    date,
  labour_hire_licence: text(200),
  licence_expiry:      date,
  notes:               text(5000),
  /** The whole title list: titles left out are removed (refused while a worker is on one). */
  titles:              z.array(ContractorTitleInputSchema).max(100).default([]),
  /** The whole worker list: workers left out are removed. */
  staff:               z.array(ContractorStaffInputSchema).max(500).default([]),
});
export type ContractorUpsert = z.infer<typeof ContractorUpsertSchema>;

export interface ContractorStaff {
  readonly id: number;
  /** The worker's staff profile — the timesheet's staff_id. */
  readonly profile_id: string;
  readonly first_name: string;
  readonly last_name: string;
  /** The worker's contractor title (null for a worker not yet put on one). */
  readonly contractor_title_id: number | null;
  readonly title: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly licence: string | null;
  readonly notes: string | null;
}

export interface Contractor {
  readonly id: number;
  readonly name: string;
  readonly abn: string | null;
  readonly address: string | null;
  readonly suburb: string | null;
  readonly state: string | null;
  readonly postcode: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly contact_name: string | null;
  readonly contact_mobile: string | null;
  readonly insurer: string | null;
  readonly insurance_policy: string | null;
  readonly insurance_expiry: string | null;
  readonly labour_hire_licence: string | null;
  readonly licence_expiry: string | null;
  readonly notes: string | null;
  readonly created_by: string | null;
  readonly updated_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly titles: readonly ContractorTitle[];
  readonly staff: readonly ContractorStaff[];
}

export interface ContractorTitle {
  readonly id: number;
  readonly title: string;
  readonly pay_rate: number | null;
  readonly base_title_id: number;
}

/** A job title a contractor worker can be on (company titles whose staff don't sign in). */
export interface ContractorWorkerTitle {
  readonly id: number;
  readonly title: string;
  /** The company title's rate — copied into a new contractor's default titles. */
  readonly pay_rate: number | null;
}
